/* eslint no-param-reassign: 0, import/no-cycle: 0, sort-imports-es6-autofix/sort-imports-es6: 0 */
import { defineStore } from 'pinia';
import { UiEvents, anonymousIdentify } from '@jumpcloud/ui-events-vue3';
import { validate as uuidValidate, v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import SignupApi from 'client/signup/api/SignupApi';
import router from 'client/signup/routes/Router';
import {
  LocalStorageConsts,
  RouteGenerator,
} from 'client/util/Constants';
import SignupConstants from 'client/util/Constants/SignupConstants';
import StoreConfigConstants from 'client/util/Constants/StoreConfigConstants';
import Util from 'client/util/Util';

const {
  segmentMap,
  partnerMarketing,
  visitorId,
  segmentUuid,
} = LocalStorageConsts;
const { adminPortalLandingPage, mtpLandingPage, userPortalActivation } = RouteGenerator;

const { shuffleArray } = Util;

const {
  clientType,
  clientTypeCookie,
  dataCenterRegions,
  freeEmails,
  gtmEvents,
  SignupOptions,
} = SignupConstants;

const { admin } = clientType;

const {
  recaptchaState,
  utmQueryFieldConsts,
  utmMarketingFields,
} = StoreConfigConstants;

const utmQueryFields = [
  ...Object.values(utmQueryFieldConsts),
];

function createInitialUser() {
  return {
    companyName: '',
    country: '',
    state: '',
    companySize: '',
    dataCenter: null,
    directory: [],
    doubleOptIn: true,
    endpointPotential: '',
    email: '',
    firstName: '',
    hasDirectory: '',
    lastName: '',
    isValid: false,
    isPartner: '',
    partnerType: '',
    phone: '',
    hasPhonePreference: false,
    recaptchaToken: '',
    useCase: '',
    termsOfService: false,
    targetEnv: 'prd',
  };
}

export function getUtmFields() {
  const utmData = {};

  utmQueryFields.forEach((fieldName) => {
    const record = JSON.parse(localStorage.getItem(fieldName));
    const marketingFieldName = utmMarketingFields[fieldName];
    if (record) {
      utmData[marketingFieldName] = record;
    }
  });

  return utmData;
}

function makeEventPayload(user) {
  return {
    companyName: user.companyName,
    companySize: user.companySize,
    country: user.country,
    dataCenter: user.dataCenter,
    state: user.state,
    email: user.email,
    endpointPotential: user.endpointPotential,
    firstName: user.firstName,
    lastName: user.lastName,
    partnerType: user.partnerType,
    phone: user.phone,
    hasPhonePreference: user.hasPhonePreference,
    doubleOptIn: user.doubleOptIn,
  };
}

export const useSignupStore = defineStore('signup', {
  state: () => ({
    growthData: {
      experimentStates: {},
      onboardingState: {},
    },
    isMsp: false,
    hasEmailQueryParam: false,
    hasLoaded: false,
    isMobile: false,
    isRestoredFromRedirect: false,
    formResetFromConfirmPending: false,
    marketingData: {
      Free_Account__c: 'True',
      GCLID__c: '',
      Initial_Campaign_Source__c: '',
      LeadSource: 'Free Account',
      Lead_Type__c: '',
      Referral_Source__c: '',
      gclid_field: '',
      license: 'on',
      signup: true,
      FreeAccount_UTM_Campaign__c: '',
      FreeAccount_UTM_Content__c: '',
      FreeAccount_UTM_Medium__c: '',
      FreeAccount_UTM_Source__c: '',
      FreeAccount_UTM_Term__c: '',
      first_touch__c: '',
      first_touch_date_time__c: '',
      segmentUuid: '',
    },
    query: {
      cs: 'Free.Account.Website',
      email: '',
      exp: '',
      rs: 'Website',
      variation: '',
      visitorId: '',
    },
    recaptchaLoaded: recaptchaState.NOT_LOADED,
    recaptchaExpired: true,
    registerError: '',
    step: 1,
    totalSteps: 4,
    useCaseDisplayText: '',
    user: createInitialUser(),
    visitorId: '',
    isEmailValid: false,
  }),

  getters: {
    getGrowthData() {
      try {
        const currentSegmentMap = get(this.growthData, 'onboardingState.segmentMap', {});
        const signupSegmentMap = JSON.parse(sessionStorage.getItem(segmentMap));
        if (isEmpty(currentSegmentMap) && signupSegmentMap) {
          this.growthData.onboardingState.segmentMap = signupSegmentMap;
        }
      } catch (e) {
        // catch and move on
      }
      return this.growthData;
    },

    getPartnerOptions() {
      return shuffleArray(SignupOptions.partnerOptions);
    },

    getPartnerTypeOptions() {
      return SignupOptions.partnerTypeOptions;
    },

    getSegmentUuid() {
      if (!this.marketingData.segmentUuid) {
        // Check if segmentUuid is stored in local storage
        let storedId = '';
        storedId = localStorage.getItem('segmentUuid') || '';

        if (storedId !== '') {
          return storedId;
        }

        // Otherwise, check for query string param
        const urlParams = new URLSearchParams(window.location.search);
        const marketingSegmentUuid = urlParams.get('jcsgmtuuid');

        // Return query string uuid if exists, otherwise return empty string
        return marketingSegmentUuid || storedId;
      }

      return this.marketingData.segmentUuid;
    },

    getTargetEnv() {
      return this.user.targetEnv;
    },

    getVisitorId() {
      if (!this.visitorId) {
        // Check if visitorId is stored in local storage
        let storedId = '';

        try {
          storedId = JSON.parse(localStorage.getItem(visitorId)).toString();
        } catch {
          // in case we get invalid JSON from legacy visitors, let storedId remain empty and move on
        }

        // validate uuid for legacy return visitors
        if (storedId && uuidValidate(storedId)) {
          return storedId;
        }

        // Otherwise, associate user IDs between Marketing site and Signup if pre-existing
        const urlParams = new URLSearchParams(window.location.search);
        let userId = urlParams.get('jcexpuuid');

        if (!userId) {
          userId = uuidv4();
        }

        return userId;
      }

      return this.visitorId;
    },
  },

  actions: {
    addExperimentToGrowthData(experiment) {
      if (!isEmpty(experiment) && !isEmpty(experiment.variation)) {
        const experimentToAdd = {
          experimentName: experiment.experimentName,
          variationState: {
            variationId: experiment.variation.id,
            variationName: experiment.variation.name,
          },
        };
        const experimentId = experiment.id;
        this.growthData.experimentStates[experimentId] = experimentToAdd;
      }
    },

    backStep(step) {
      const userInfo = makeEventPayload(this.user);
      EventHelper.triggerSignupStepBack({ step, userInfo });
    },

    clearUtmValues() {
      utmQueryFields.forEach((fieldName) => {
        localStorage.removeItem(fieldName);
        this.updateParsedUtmQueryValue({ name: fieldName, value: '' });
      });
    },

    completeStep(step) {
      const userInfo = makeEventPayload(this.user);
      EventHelper.triggerSignupStepCompleted({ step, userInfo });
    },

    getGclidRecord() {
      const gclidParam = this.query.gclid;
      const gclsrcParam = this.query.gclsrc;

      const isGclsrcValid = !gclsrcParam || gclsrcParam.indexOf('aw') !== -1;
      let gclidRecord;

      // Check if gclid is in URL param; if so, save and store in local storage
      if (gclidParam && isGclsrcValid) {
        const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000); // 90 day expiry
        gclidRecord = {
          value: gclidParam,
          expiryDate,
        };
        localStorage.setItem('gclid', JSON.stringify(gclidRecord));
      }

      // Check if gclid is stored in local storage; if so, use that
      const gclid = gclidRecord || JSON.parse(localStorage.getItem('gclid'));
      if (gclid && new Date().getTime() < gclid.expiryDate) {
        this.updateGclid(gclid.value);
      }
    },

    parseUtmQueryFields() {
      let utmFieldsReceived = false;
      // Sometimes other JC pages give us UTMs or First Touch values which are not lowercase
      // We want to accept all of them if they match the key
      // In order to do that we translate the keys to be lowercase so we can then parse the values
      const lowercaseQueryObject = {};
      Object.keys(this.query).forEach((queryKey) => {
        lowercaseQueryObject[queryKey.toLowerCase()] = this.query[queryKey];
      });

      // Parse all utmQueryFields when signup loads
      // This will update with new values from the query params and load old values into the store
      utmQueryFields.forEach((fieldName) => {
        try {
          let fieldRecord;
          const fieldValue = lowercaseQueryObject[fieldName];

          if (fieldValue) {
            const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000); // 90 day expiry
            fieldRecord = {
              value: fieldValue,
              expiryDate,
            };

            localStorage.setItem(fieldName, JSON.stringify(fieldRecord));

            // If we're here, we recieved a utm value in the query params
            utmFieldsReceived = true;
          }

          const record = fieldRecord || JSON.parse(localStorage.getItem(fieldName));
          if (record && new Date().getTime() < record.expiryDate) {
            this.updateParsedUtmQueryValue({ name: fieldName, value: record.value });
          }
        } catch (e) {
          // just catch and move on if there's an error
        }
      });

      if (utmFieldsReceived) {
        EventHelper.triggerSignupUtmData(getUtmFields());
      }
    },

    setRecaptchaLoaded(value) {
      if (recaptchaState[value]) {
        this.recaptchaLoaded = value;
      }
    },

    setRecaptchaToken(value) {
      this.user.recaptchaToken = value;
    },

    setRecaptchaExpired(value) {
      this.recaptchaExpired = value;
    },

    setStep(step) {
      this.step = step ? parseInt(step, 10) : 1;
    },

    setTargetEnv(dataCenter) {
      if (!dataCenter) {
        // Don't overwrite cookie when dataCenter is cleared (e.g. after country change).
        // Preserves correct env on current domain until user reselects data center.
        return;
      }
      const env = dataCenterRegions[dataCenter]?.environment ?? 'prd';
      this.user.targetEnv = env;
      this.setTargetEnvCookie(env);
    },

    initializeTargetEnv() {
      this.setTargetEnvCookie('prd');
    },

    setValidation(value) {
      this.user.isValid = value;
    },

    sendInputEnteredEvent({ name, label }) {
      const formPayload = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        isPartner: this.user.isPartner,
        partnerType: this.user.partnerType,
        companyName: this.user.companyName,
        companySize: this.user.companySize,
        endpointPotential: this.user.endpointPotential,
        country: this.user.country,
        state: this.user.state,
        dataCenter: this.user.dataCenter,
        doubleOptIn: this.user.doubleOptIn,
        phone: this.user.phone,
        hasPhonePreference: this.user.hasPhonePreference,
        email: {
          email: this.user.email,
          isValid: this.isEmailValid,
        },
        termsOfService: this.user.termsOfService,
      };
      const eventPayload = {
        description: `${name} input`,
        page: 'Signup Page',
        section: 'Signup Form',
        text: label,
        value: formPayload,
      };
      UiEvents.triggerInputEntered(eventPayload);
    },

    async submitRegistration(nextLink) {
      const {
        marketingData = {},
        registerError,
        user,
      } = this;

      const eventPayload = makeEventPayload(user);

      if (registerError) this.registerError = '';
      const utmData = getUtmFields();
      EventHelper.triggerSignupSubmitted({ ...eventPayload, ...utmData });
      anonymousIdentify({
        traits: {
          ...eventPayload,
          ...marketingData,
        },
      });

      const selectedDirectories = [];
      if (user.directory.length) {
        user.directory.forEach((directory) => {
          selectedDirectories.push(directory.value);
        });
      }

      const payload = {
        ...marketingData,
        company: user.companyName,
        company_size: user.companySize,
        email: user.email,
        Endpoint_Potential__c_lead: user.endpointPotential,
        first_name: user.firstName,
        growthData: this.getGrowthData,
        last_name: user.lastName,
        sendRegistrationCode: true,
        termsOfService: user.termsOfService,
        Country: user.country,
        dataCenter: user.dataCenter || 'us',
        Directory__c: selectedDirectories,
        Double_Opt_In__c: user.doubleOptIn,
        Phone: user.phone,
        PhonePreference: user.hasPhonePreference,
        Use_Case__c: user.useCase,
        'g-recaptcha-response': user.recaptchaToken,
        entitlement_source: 'Sign Up',
      };

      if (user.isPartner === 'Yes') {
        payload.Lead_Type__c = user.partnerType;
        if (user.partnerType === 'MSP') {
          payload.entitlement_source = 'MTP Tenant';
        }
      } else {
        payload.Lead_Type__c = '';
      }

      if (user.state) {
        payload.state = user.state;
      }

      try {
        await SignupApi.register(payload);

        try {
          // Trigger Segment event for account creation
          await EventManager.triggerAccountCreated(eventPayload);
          // Trigger GTM event for account creation
          if (window.dataLayer) {
            window.dataLayer.push(gtmEvents.freeAccountTrackingEvent);
          }
        } catch (e) {
          // Do nothing, just catch for safety and move on
        }

        this.clearUtmValues();
        this.setValidation(true);
        router.push(nextLink).catch((err) => {
          // Ignore NavigationDuplicated for CircleCI
          if (
            err.name !== 'NavigationDuplicated'
            && !err.message.includes('Avoided redundant navigation to current location')
          ) {
            // But throw any other errors
            throw err;
          }
        });
      } catch (error) {
        const defaultErrorMessage = 'There was an error registering your account. Please try again.';
        const errorMessage = get(error, 'response.data.message', defaultErrorMessage);
        this.registerError = errorMessage;
        throw errorMessage;
      }
    },

    async confirmPartnerRegistration({ code, emailAddress }) {
      const payload = {
        companySize: this.user.companySize,
        country: this.user.country,
        email: emailAddress,
        partnerType: this.user.partnerType,
        registrationCode: code,
      };

      // save the form responses incase the verifcation fails
      try {
        sessionStorage.setItem(partnerMarketing, JSON.stringify(payload));
      } catch (e) {
        // catch and move on
      }

      const res = await SignupApi.confirmPartnerRegistration(payload);
      return this.triggerVerificationEventsAndRedirect({ emailAddress, confirmResponse: res });
    },

    async confirmRegistration({ code, emailAddress }) {
      const res = await SignupApi.confirmRegistrationCode(emailAddress, code);
      return this.triggerVerificationEventsAndRedirect({ emailAddress, confirmResponse: res });
    },

    triggerVerificationEventsAndRedirect({ confirmResponse, emailAddress }) {
      // We're not resolving or rejecting as we should redirect
      // if there's an error the caller should catch it
      // Disable rule for not having async promise functions
      // eslint-disable-next-line
      return new Promise(async (resolve) => {
        try {
          // Trigger Segment event
          const { triggerAccountVerified } = EventManager;
          triggerAccountVerified({
            email: emailAddress,
          });

          // Trigger GTM event
          if (window.dataLayer) {
            window.dataLayer.push(gtmEvents.accountVerified);
            // Enhanced tracking helps Google Analytics track conversions
            // across domains/browers/devices
            // At this time (Dec-2022), it only works for gmail accounts
            // We still want to trigger the event for all email and GA will sort it out
            // If support for business email is added this implementation will automatically improve
            const googleEnhancedConversionDataEventCopy = {
              ...gtmEvents.googleEnhancedConversionDataEvent,
            };
            googleEnhancedConversionDataEventCopy.enhanced_conversion_data = {
              email: emailAddress,
            };
            window.dataLayer.push(googleEnhancedConversionDataEventCopy);

            const isFreeEmail = freeEmails.some((email) => emailAddress.indexOf(email) >= 0);
            if (isFreeEmail) {
              // Trigger Free domain VFA GTM event
              window.dataLayer.push(gtmEvents.accountVerifiedFreeEmail);
            } else {
              window.dataLayer.push(gtmEvents.accountVerifiedBusinessDomain);
            }
          }
        } catch (e) {
          // Do nothing, just catch for safety and move on
        }
        // Redirect admin after a short timeout
        setTimeout(() => {
          sessionStorage.removeItem(partnerMarketing);
          try {
            Cookies.set(clientTypeCookie, admin);
          } catch (e) {
            // Do nothing, just catch for safety and move on
          }
          const { lastUrl } = (confirmResponse && confirmResponse.data) || {};
          let redirectUrl;

          if (lastUrl && lastUrl === mtpLandingPage) {
            redirectUrl = mtpLandingPage;
          } else if (lastUrl && lastUrl.includes(userPortalActivation)) {
            redirectUrl = lastUrl;
          } else {
            redirectUrl = adminPortalLandingPage;
          }

          Util.assignLocation(redirectUrl);
          resolve();
        }, 1000);
      });
    },

    // Helper action methods for state updates
    updateHasLoaded(value) {
      this.hasLoaded = value;
    },

    updateHasEmailQueryParam(value) {
      this.hasEmailQueryParam = value;
    },

    updateQueryStrings(query) {
      const updatedQuery = { ...this.query, ...query };
      this.query = updatedQuery;
      this.marketingData.Initial_Campaign_Source__c = updatedQuery.cs;
      this.marketingData.Referral_Source__c = updatedQuery.rs;
      this.getGclidRecord();
      this.parseUtmQueryFields();
    },

    updateIsMsp(value) {
      this.isMsp = value;
    },

    updateReferrer(referralSource) {
      // update if not previously updated
      const currentSignupReferralUrl = get(this.growthData, 'onboardingState.signupReferralUrl', '');
      if (!currentSignupReferralUrl) {
        const signupReferralUrl = referralSource || get(window, 'document.referrer', '');
        const trimmedSignupReferralUrl = signupReferralUrl.split('?')[0];

        // Pass the signupReferralUrl in for all signups
        // Let admin portal logic determine what to do
        this.growthData.onboardingState.signupReferralUrl = trimmedSignupReferralUrl;
      }
    },

    updateUseCase(value) {
      // useCase is the mapped useCase value that gets sent to the backend
      // useCaseDisplayText is what was displayed on the button selected
      // some display elements need to know
      this.user.useCase = value.useCase;
      this.useCaseDisplayText = value.useCaseDisplayText;
    },

    updateEmail(email) {
      this.user.email = email;
    },

    updateEmailValid(value) {
      this.isEmailValid = value;
    },

    updateVisitorId(id) {
      this.visitorId = id;
      localStorage.setItem(visitorId, JSON.stringify(id));
    },

    updateSegmentUuid(id) {
      this.marketingData.segmentUuid = id;
      localStorage.setItem(segmentUuid, id);
    },

    updateIsMobile(value) {
      this.isMobile = value;
    },

    updatePartnerResponses(email) {
      const payload = JSON.parse(sessionStorage.getItem(partnerMarketing));

      if (payload && payload.email === email) {
        this.user.companySize = payload.companySize;
        this.user.country = payload.country;
        this.user.partnerType = payload.partnerType;

        if (payload.partnerType) {
          this.user.isPartner = 'Yes';
        } else {
          this.user.isPartner = 'No';
        }
      }
    },

    updateSegmentMap(value) {
      this.growthData.onboardingState.segmentMap = value;
      try {
        sessionStorage.setItem(segmentMap, JSON.stringify(value));
      } catch (e) {
        // catch and move on
      }
    },

    writeToGrowthObject(newData) {
      const newGrowthObject = { ...cloneDeep(this.growthData), ...newData };
      this.growthData = newGrowthObject;
    },

    // Helper methods for internal use
    updateGclid(value) {
      this.marketingData.GCLID__c = value;
      this.marketingData.gclid_field = value;
    },

    updateParsedUtmQueryValue(fieldData) {
      const fieldName = fieldData.name;
      const fieldValue = fieldData.value;
      const marketingFieldName = utmMarketingFields[fieldName];
      if (marketingFieldName) {
        this.marketingData[marketingFieldName] = fieldValue;
      }
    },

    setTargetEnvCookie(env) {
      Cookies.set('_jc_target_env', env);
    },

    /** Router calls this when returning from confirm so OnePageForm can reset on mount. */
    armFormResetFromConfirm() {
      this.formResetFromConfirmPending = true;
    },

    resetSignupForm() {
      Object.assign(this.user, createInitialUser());
      this.registerError = '';
      this.isEmailValid = false;
      this.isRestoredFromRedirect = false;
      this.setRecaptchaLoaded(recaptchaState.NOT_LOADED);
      this.setRecaptchaExpired(true);
    },

    /** OnePageForm: returns whether to reset, then clears the flag (single consumer). */
    takeFormResetFromConfirm() {
      const pending = this.formResetFromConfirmPending;
      this.formResetFromConfirmPending = false;
      return pending;
    },

    restoreFormDataFromRedirect() {
      try {
        // Check for redirect_data in URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const redirectData = urlParams.get('redirect_data');

        if (redirectData) {
          // Decode the base64 encoded form data with Unicode support using TextDecoder
          const binaryString = atob(redirectData);
          const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
          const decodedData = new TextDecoder().decode(bytes);
          const formData = JSON.parse(decodedData);

          // Whitelist of allowed fields that can be restored from redirect
          // This prevents attackers from manipulating sensitive fields like
          // isValid, termsOfService, recaptchaToken, or targetEnv
          const allowedFields = [
            'firstName',
            'lastName',
            'companyName',
            'companySize',
            'endpointPotential',
            'country',
            'state',
            'email',
            'phone',
            'hasPhonePreference',
            'isPartner',
            'partnerType',
            'dataCenter',
            'doubleOptIn',
          ];

          // Restore only whitelisted user data to the store
          // Check for existence rather than truthiness to preserve false/0 values
          allowedFields.forEach((key) => {
            if (formData[key] !== undefined
              && formData[key] !== null
              && formData[key] !== ''
            ) {
              this.user[key] = formData[key];
            }
          });

          // Set flag to indicate data was restored from redirect
          this.isRestoredFromRedirect = true;

          // Clean up URL by removing the redirect_data parameter
          urlParams.delete('redirect_data');
          const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
          window.history.replaceState({}, '', newUrl);

          // Return true to indicate data was restored
          return true;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to restore form data from redirect:', e);
      }

      return false;
    },
  },
});
